import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getCollectionHandle } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { DateTime } from 'luxon';
import { from, switchMap } from 'rxjs';
import { Workshop } from '../entities/Workshop.js';
import { WorkshopAttendance } from '../entities/WorkshopAttendance.js';
import { WorkshopAttendanceStatus } from '../entities/WorkshopAttendanceStatus.js';

@Entity()
export class WorkshopOnboardingInfo extends AdornisEntity {
  public static override _class = 'WorkshopOnboardingInfo';

  static override resolveID(entity: WorkshopOnboardingInfo): string {
    return (entity.userID ?? A.getGloballyUniqueID()) + '_' + (entity.workshopID ?? A.getGloballyUniqueID());
  }

  @Field(() => String)
  public userID: Maybe<string>;

  @Field(() => String)
  public workshopID: Maybe<string>;

  @DataLoadedField(() => Workshop, ({ workshopID }) => workshopID)
  public workshop: Maybe<Workshop>;

  @Field(() => String)
  public state: Maybe<WorkshopAttendanceStatus>;

  @Field(() => DateTime)
  public date: Maybe<DateTime>;
}

export const subscribeWorkshopOnboarding = registerSubscription({
  type: () => [WorkshopOnboardingInfo],
  operationName: 'subscribeWorkshopOnboarding',
  resolve: (skip: number, limit: number, userID: string) => {
    return (gqlFields: BaseQLSelectionSet<WorkshopOnboardingInfo>) => {
      const selector = { _class: WorkshopAttendance._class, userID };
      const handle = getCollectionHandle<WorkshopAttendance>(WorkshopAttendance._collectionName);
      return from(publishFilter(WorkshopAttendance._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector, { skip, limit });
        }),
        switchMap(async attendances => {
          const workshops = await loadMongoData(Workshop, { showAsOnboarding: true });

          (attendances as unknown as EntityData<WorkshopAttendance>[]).sort((a, b) => {
            return (
              DateTime.fromJSDate(a.date ?? new Date()).toMillis() -
              DateTime.fromJSDate(b.date ?? new Date()).toMillis()
            );
          });

          return workshops.map(workshop => {
            const found =
              attendances.find(x => x.workshopID === workshop._id && x.state === WorkshopAttendanceStatus.ATTENDED) ??
              attendances.find(x => x.workshopID === workshop._id);

            return new WorkshopOnboardingInfo({
              userID,
              workshopID: workshop._id,
              workshop,
              state: found?.state === WorkshopAttendanceStatus.ATTENDED ? WorkshopAttendanceStatus.ATTENDED : undefined,
              date: found?.state === WorkshopAttendanceStatus.ATTENDED ? found.date : undefined,
            });
          });
        }),
      );
    };
  },
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'userID', type: () => String },
  ],
});
